import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card"
import {
    ImageIcon,
    BriefcaseIcon,
    QrCodeIcon,
    ShareIcon,
    CalendarIcon,
    SearchIcon,
    LucideProps
} from "lucide-react"
import { ForwardRefExoticComponent, RefAttributes } from "react";
import { useTranslation } from "react-i18next";

type Feature = {
    icon: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>
    titleKey: string
    descriptionKey: string
    gradientIndex: number
    image?: string
};

const features: Feature[] = [
    {
        icon: ImageIcon,
        titleKey: "features.portfolio.title",
        descriptionKey: "features.portfolio.description",
        gradientIndex: 0,  // Emerald
    },
    {
        icon: BriefcaseIcon,
        titleKey: "features.services.title",
        descriptionKey: "features.services.description",
        gradientIndex: 1,  // Purple
        // image: "https://picsum.photos/seed/portfolio/200/150",
    },
    {
        icon: QrCodeIcon,
        titleKey: "features.qrCode.title",
        descriptionKey: "features.qrCode.description",
        gradientIndex: 2,  // Pink
    },
    {
        icon: ShareIcon,
        titleKey: "features.socialMedia.title",
        descriptionKey: "features.socialMedia.description",
        gradientIndex: 0,  // Emerald
    },
    {
        icon: CalendarIcon,
        titleKey: "features.availability.title",
        descriptionKey: "features.availability.description",
        gradientIndex: 1,  // Purple
    },
    {
        icon: SearchIcon,
        titleKey: "features.seo.title",
        descriptionKey: "features.seo.description",
        gradientIndex: 2,  // Pink
    },
]

export function ValueProposition() {
    const { t } = useTranslation('landing')

    return (
        <section id="features" className="py-20 bg-gradient-to-br from-pink-500/15 to-emerald-500/25">
            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                        {t('features.title')}
                    </h2>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        {t('features.subtitle')}
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {features.map((feature, index) => (
                        <Card
                            key={feature.titleKey}
                            className={`
                border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg overflow-hidden
                hover:border-emerald-500/50 hover:shadow-emerald-500/10
              `}
                            style={{ animationDelay: `${index * 100}ms` }}
                        >
                            {feature.image ? (
                                <>
                                    <img
                                        src={feature.image}
                                        alt={t(feature.titleKey)}
                                        className="w-full h-48 object-cover rounded-t-lg"
                                    />
                                    <div className="p-6">
                                        <h3 className="text-xl font-semibold mb-2">{t(feature.titleKey)}</h3>
                                        <p className="text-muted-foreground">{t(feature.descriptionKey)}</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <CardHeader>
                                        <div className={`
                      w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-md
                      ${index % 3 === 0 ? 'bg-gradient-to-br from-emerald-500/20 to-emerald-500/5' : ''}
                      ${index % 3 === 1 ? 'bg-gradient-to-br from-purple-500/20 to-purple-500/5' : ''}
                      ${index % 3 === 2 ? 'bg-gradient-to-br from-pink-500/20 to-pink-500/5' : ''}
                    `}>
                                            <feature.icon className="w-7 h-7 text-foreground" />
                                        </div>
                                        <CardTitle className="text-xl">{t(feature.titleKey)}</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-muted-foreground">{t(feature.descriptionKey)}</p>
                                    </CardContent>
                                </>
                            )}
                        </Card>
                    ))}
                </div>
            </div>
        </section>
    )
}
